import React, { useState, useEffect } from 'react';
import { getGalleriaFiles, getUserDisplayName } from '../../../firebase';
import { useAuth } from '../../../contexts/AuthContext';
import './GalleriaCantiere.css';

const GalleriaCantiere = ({ cantiere, onBack }) => {
  const { currentUser } = useAuth();
  
  // Stati principali
  const [files, setFiles] = useState([]);
  const [filteredFiles, setFilteredFiles] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedFile, setSelectedFile] = useState(null);
  
  // Stati per filtri e visualizzazione
  const [viewMode, setViewMode] = useState('grid'); // 'grid', 'list'
  const [fileTypeFilter, setFileTypeFilter] = useState('all'); // 'all', 'images', 'pdf'
  const [dateFilter, setDateFilter] = useState('');
  const [sortBy, setSortBy] = useState('data-desc'); // 'data-desc', 'data-asc', 'nome-asc', 'nome-desc'
  const [searchTerm, setSearchTerm] = useState('');
  
  // Stati per statistiche
  const [stats, setStats] = useState({
    total: 0,
    images: 0,
    pdfs: 0,
    totalSize: 0
  });

  useEffect(() => {
    if (cantiere) {
      loadFiles();
    }
  }, [cantiere]);

  useEffect(() => {
    applyFilters();
  }, [files, fileTypeFilter, dateFilter, sortBy, searchTerm]);

  const loadFiles = async () => {
    try {
      setIsLoading(true);
      console.log('📂 Caricamento file per cantiere:', cantiere.nomeCommessa);
      
      const filesData = await getGalleriaFiles(cantiere.id);
      setFiles(filesData);
      
      // Calcola statistiche
      const newStats = {
        total: filesData.length,
        images: filesData.filter(f => f.isImage).length,
        pdfs: filesData.filter(f => f.isPDF).length,
        totalSize: filesData.reduce((sum, f) => sum + (f.dimensione || 0), 0)
      };
      setStats(newStats);
      
      console.log('📊 Statistiche galleria:', newStats);
      
    } catch (error) {
      console.error('❌ Errore nel caricamento file:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...files];
    
    // Filtro per tipo file
    if (fileTypeFilter === 'images') {
      filtered = filtered.filter(f => f.isImage);
    } else if (fileTypeFilter === 'pdf') {
      filtered = filtered.filter(f => f.isPDF);
    }
    
    // Filtro per data
    if (dateFilter) {
      filtered = filtered.filter(f => {
        const fileDate = new Date(f.dataCaricamento).toISOString().split('T')[0];
        return fileDate === dateFilter;
      });
    }
    
    // Filtro per ricerca testo
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      filtered = filtered.filter(f => 
        f.nome.toLowerCase().includes(searchLower) ||
        (f.descrizione && f.descrizione.toLowerCase().includes(searchLower))
      );
    }
    
    // Ordinamento
    const [field, order] = sortBy.split('-');
    filtered.sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      
      if (field === 'data' || field === 'dataCaricamento') {
        aValue = new Date(aValue);
        bValue = new Date(bValue);
      } else if (field === 'nome') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      
      if (order === 'asc') {
        return aValue > bValue ? 1 : -1;
      } else {
        return aValue < bValue ? 1 : -1;
      }
    });
    
    setFilteredFiles(filtered);
  };

  const formatDate = (date) => {
    if (!date) return 'Data non disponibile';
    return new Date(date).toLocaleDateString('it-IT', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'Dimensione sconosciuta';
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / 1024 / 1024).toFixed(1) + ' MB';
  };

  const getFileIcon = (file) => {
    if (file.isImage) return '🖼️';
    if (file.isPDF) return '📄';
    return '📁';
  };

  const downloadFile = (file) => {
    const link = document.createElement('a');
    link.href = file.url;
    link.download = file.nome;
    link.click();
  };

  if (isLoading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>📂 Caricamento galleria...</p>
      </div>
    );
  }

  return (
    <div className="galleria-cantiere">
      {/* Header */}
      <div className="gallery-header">
        <div className="header-left">
          <button className="btn-back" onClick={onBack}>
            ← Torna al Cantiere
          </button>
          <div className="header-info">
            <h1>🖼️ Galleria - {cantiere.nomeCommessa}</h1>
            <div className="stats-summary">
              <span className="stat-item">📁 {stats.total} file</span>
              <span className="stat-item">🖼️ {stats.images} immagini</span>
              <span className="stat-item">📄 {stats.pdfs} PDF</span>
              <span className="stat-item">💾 {formatFileSize(stats.totalSize)}</span>
            </div>
          </div>
        </div>
        
        <div className="header-actions">
          <button 
            className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('grid')}
            title="Vista griglia"
          >
            🔲 Griglia
          </button>
          <button 
            className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setViewMode('list')}
            title="Vista lista"
          >
            📋 Lista
          </button>
        </div>
      </div>

      {/* Filtri */}
      <div className="gallery-filters">
        <div className="filters-row">
          <div className="filter-group">
            <label>🔍 Cerca:</label>
            <input
              type="text"
              placeholder="Nome file o descrizione..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="form-control"
            />
          </div>
          
          <div className="filter-group">
            <label>📁 Tipo:</label>
            <select
              value={fileTypeFilter}
              onChange={(e) => setFileTypeFilter(e.target.value)}
              className="form-select"
            >
              <option value="all">Tutti i file</option>
              <option value="images">Solo immagini</option>
              <option value="pdf">Solo PDF</option>
            </select>
          </div>
          
          <div className="filter-group">
            <label>📅 Data:</label>
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="form-control"
            />
          </div>
          
          <div className="filter-group">
            <label>🔄 Ordina:</label>
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="form-select"
            >
              <option value="data-desc">Data (più recente)</option>
              <option value="data-asc">Data (più vecchia)</option>
              <option value="nome-asc">Nome A-Z</option>
              <option value="nome-desc">Nome Z-A</option>
            </select>
          </div>
          
          {(searchTerm || dateFilter || fileTypeFilter !== 'all') && (
            <div className="filter-group">
              <button 
                className="btn btn-secondary btn-sm"
                onClick={() => {
                  setSearchTerm('');
                  setDateFilter('');
                  setFileTypeFilter('all');
                }}
                title="Cancella filtri"
              >
                🗑️ Reset
              </button>
            </div>
          )}
        </div>
        
        <div className="filters-summary">
          <span>📊 Visualizzando {filteredFiles.length} di {files.length} file</span>
        </div>
      </div>

      {/* Contenuto Galleria */}
      {filteredFiles.length === 0 ? (
        <div className="empty-gallery">
          <div className="empty-icon">📭</div>
          <h3>Nessun file trovato</h3>
          <p>
            {files.length === 0 ? 
              'Non ci sono ancora file caricati per questo cantiere.' : 
              'Nessun file corrisponde ai filtri selezionati.'
            }
          </p>
          <p>I file vengono caricati automaticamente quando alleghi foto/PDF ai rapportini.</p>
        </div>
      ) : (
        <div className={`gallery-content ${viewMode}`}>
          {viewMode === 'grid' ? (
            <div className="files-grid">
              {filteredFiles.map((file) => (
                <div 
                  key={file.id} 
                  className="file-card"
                  onClick={() => setSelectedFile(file)}
                >
                  <div className="file-preview">
                    {file.isImage ? (
                      <img 
                        src={file.url} 
                        alt={file.nome}
                        className="preview-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="preview-placeholder">
                        <span className="file-icon">{getFileIcon(file)}</span>
                        <span className="file-type">{file.tipo}</span>
                      </div>
                    )}
                    <div className="file-overlay">
                      <span className="file-date">{formatDate(file.dataCaricamento)}</span>
                    </div>
                  </div>
                  
                  <div className="file-info">
                    <h4 className="file-name" title={file.nome}>{file.nome}</h4>
                    <div className="file-meta">
                      <span className="file-size">{formatFileSize(file.dimensione)}</span>
                      <span className="file-rapportino">
                        📝 {formatDate(file.dataRapportino)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="files-list">
              {filteredFiles.map((file) => (
                <div 
                  key={file.id} 
                  className="file-list-item"
                  onClick={() => setSelectedFile(file)}
                >
                  <div className="list-preview">
                    {file.isImage ? (
                      <img 
                        src={file.url} 
                        alt={file.nome}
                        className="list-image"
                        loading="lazy"
                      />
                    ) : (
                      <div className="list-placeholder">
                        <span className="file-icon">{getFileIcon(file)}</span>
                      </div>
                    )}
                  </div>
                  
                  <div className="list-info">
                    <h4 className="file-name">{file.nome}</h4>
                    <div className="file-details">
                      <span className="detail-item">📅 Caricato: {formatDate(file.dataCaricamento)}</span>
                      <span className="detail-item">📝 Rapportino: {formatDate(file.dataRapportino)}</span>
                      <span className="detail-item">💾 {formatFileSize(file.dimensione)}</span>
                      <span className="detail-item">📁 {file.tipo}</span>
                    </div>
                    {file.descrizione && (
                      <p className="file-description">{file.descrizione}</p>
                    )}
                  </div>
                  
                  <div className="list-actions">
                    <button 
                      className="btn btn-sm btn-secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadFile(file);
                      }}
                      title="Scarica file"
                    >
                      📥
                    </button>
                    <button 
                      className="btn btn-sm btn-primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedFile(file);
                      }}
                      title="Visualizza dettagli"
                    >
                      👁️
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Modal per visualizzazione file */}
      {selectedFile && (
        <div className="file-modal-overlay" onClick={() => setSelectedFile(null)}>
          <div className="file-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3>{getFileIcon(selectedFile)} {selectedFile.nome}</h3>
              <button 
                className="modal-close"
                onClick={() => setSelectedFile(null)}
              >
                ✖️
              </button>
            </div>
            
            <div className="modal-content">
              {selectedFile.isImage ? (
                <img 
                  src={selectedFile.url} 
                  alt={selectedFile.nome}
                  className="modal-image"
                />
              ) : (
                <div className="modal-file-preview">
                  <div className="file-icon-large">{getFileIcon(selectedFile)}</div>
                  <h4>{selectedFile.nome}</h4>
                  <p>Tipo: {selectedFile.tipo}</p>
                  <p>Dimensione: {formatFileSize(selectedFile.dimensione)}</p>
                </div>
              )}
            </div>
            
            <div className="modal-info">
              <div className="info-grid">
                <div className="info-item">
                  <strong>📅 Data caricamento:</strong>
                  <span>{formatDate(selectedFile.dataCaricamento)}</span>
                </div>
                <div className="info-item">
                  <strong>📝 Rapportino del:</strong>
                  <span>{formatDate(selectedFile.dataRapportino)}</span>
                </div>
                <div className="info-item">
                  <strong>💾 Dimensione:</strong>
                  <span>{formatFileSize(selectedFile.dimensione)}</span>
                </div>
                <div className="info-item">
                  <strong>📁 Tipo file:</strong>
                  <span>{selectedFile.tipo}</span>
                </div>
                {selectedFile.descrizione && (
                  <div className="info-item full-width">
                    <strong>📄 Descrizione:</strong>
                    <span>{selectedFile.descrizione}</span>
                  </div>
                )}
              </div>
            </div>
            
            <div className="modal-actions">
              <a 
                href={selectedFile.url} 
                download={selectedFile.nome}
                className="btn btn-primary"
                onClick={(e) => e.stopPropagation()}
              >
                📥 Scarica
              </a>
              <a 
                href={selectedFile.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="btn btn-secondary"
                onClick={(e) => e.stopPropagation()}
              >
                🔗 Apri in nuova finestra
              </a>
              <button 
                className="btn btn-secondary"
                onClick={() => setSelectedFile(null)}
              >
                ❌ Chiudi
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GalleriaCantiere;